"use client";
import React, { useState, useEffect } from "react";
import { IVariantDetailsPrice } from "../CustomCard/CustomCard.types";
import CustomCard from "../CustomCard";
import styles from "./CardListPrice.module.scss";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { postLead } from "@services/leadCaptureService";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { getPlatform } from "@utils/platformDetection.client";
import { getLeadData, setLeadData } from "@utils/leadDataStorage";

interface CardListProps {
  variants: IVariantDetailsPrice[];
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
}

export default function CardList({
  variants,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance
}: CardListProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [jsEnabled, setJsEnabled] = useState(false);
  
  const variantsToShow = variants;

  // Mark JS as enabled when component mounts
  useEffect(() => {
    setJsEnabled(true);
  }, []);
  const handleToggle = async (index: number) => {
    const prevIdx = expandedIndex;
    const variantName = variantsToShow?.[index]?.variantName;
    setExpandedIndex((prev) => (prev === index ? null : index));
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_variant",
        car_availability: carAvailability,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        city_name: city?.name,
        Section: "Price page summary",
      },
    });
    if (prevIdx !== index && isLoggedIn) {
      let leadData: any = {
        city: city?.name,
        make: brandName || "",
        model: modelName || "",
        variant: variantName || "",
        color: "",
      };
      // const storedSource = sessionStorage.getItem("referrerSource");
      const utmTerm =
        new URL(window.location.href).searchParams.get("utm_term") || "";
      leadData.dump_data = {
        mx_Platform: getPlatform(),
        mx_Page_Type_Web: pageType,
        mx_Login_Type: "Viewed detailed price",
        // SourceReferrerURL: storedSource,
        mx_URL: window.location.href || "https://ackodrive.com",
        mx_UTM_Term: utmTerm,
        mx_color: leadData?.color || "",
      };

      // Get existing lead data with fallback to localStorage
      const existingLeadData = await getLeadData();
      const mergedLeadData = { ...existingLeadData, ...leadData };
      leadData = mergedLeadData;
      
      // Save to both storages
      setLeadData(mergedLeadData);
      const buyingPeriod = leadData?.buying_period || "";
      try {
        const leadResponse = await postLead(addUTMParamsToLeadData(leadData));
        if (!leadResponse.success) {
          console.error("Lead capture failed.", leadResponse);
          await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              exception_type: "API issue",
              exception_name: "failed lead creation",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: city?.name,
              login_type: "Viewed detailed price",
              intent: buyingPeriod,
            },
          });
        } else {
          const leadCreated = !!leadResponse?.data?.lead_id;
          await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              action_type: leadCreated ? "lead_created" : "lead_updated",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: city?.name,
              login_type: "Viewed detailed price",
              intent: buyingPeriod,
            },
          });
        }
      } catch (error) {
        console.error("Error in postLead API call:", error);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "failed lead creation",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: variantName,
            city_name: city.name,
            login_type: "Viewed detailed price",
            intent: buyingPeriod
          },
        });
      }
    }
  };

  return (
    <div className={styles.container}>
            <div className={`${styles.cardWrapper} ${jsEnabled && !showMore ? styles.hideExtraVariants : ""}`}>  
        {variantsToShow.map((variant, index) => (
          <div
            key={variant.slug}
            className={jsEnabled && index >= 4 && !showMore ? styles.hiddenVariant : ""}
          >
            <CustomCard
            data={variant}
            isExpanded={expandedIndex === index}
            onToggle={() => handleToggle(index)}
            isLoggedIn={isLoggedIn}
            city={city}
            brandName={brandName}
            modelName={modelName}
            isSellable={variant.status === "sellable"}
            pageType={pageType}
            carAvailability={carAvailability}
            savingBalance={savingBalance}
          />
          </div>
        ))}
      </div>
      {jsEnabled && variants.length > 4 && (
        <div
          className={styles.viewMoreLink}
          onClick={() => setShowMore(!showMore)}
        >
          {showMore
            ? `View less variants`
            : `View ${variants.length - 4} more variants`}
          <Image
            src={`${assetLoader()}/icons/Chevron_down.svg`}
            alt="arrow-right"
            width={24}
            height={24}
            className={`${showMore ? styles.iconRotate : ""}`}
          />
        </div>
      )}
    </div>
  );
}
